import React from 'react';

import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import type { UnmatchedFlashPackage } from './PackageSetupStep/utils';

interface Props {
  disabled?: boolean;
  onSelectRoomType: (packageId: string, roomTypeId: string) => void;
  roomTypeOptions: Array<{ id: string; name: string }>;
  selectedRoomTypeIdByPackageId: Record<string, string>;
  unmatchedPackages: Array<UnmatchedFlashPackage>;
}

// Only failures fixable by picking a room type: needs a persist id (to save the pick) and a
// Salesforce room name that failed to auto-match.
function getPickableUnmatchedPackages(unmatchedPackages: Array<UnmatchedFlashPackage>) {
  return unmatchedPackages.filter(
    (unmatchedPackage): unmatchedPackage is UnmatchedFlashPackage & { packageId: string; roomAssetName: string } =>
      !!unmatchedPackage.packageId && !!unmatchedPackage.roomAssetName,
  );
}

export default function UnmatchedPackageRoomPicker({
  disabled,
  onSelectRoomType,
  roomTypeOptions,
  selectedRoomTypeIdByPackageId,
  unmatchedPackages,
}: Props) {
  const pickablePackages = getPickableUnmatchedPackages(unmatchedPackages);

  if (pickablePackages.length === 0 || roomTypeOptions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Map to an existing room type
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        The Salesforce room name below didn&apos;t confidently match one of your room types. Pick the correct room type
        for each package, then retry linking.
      </Typography>

      <Stack spacing={1.5} mt={1.5}>
        {pickablePackages.map((unmatchedPackage) => {
          const selectId = `unmatched-package-room-${unmatchedPackage.packageId}`;

          return (
            <Stack
              key={unmatchedPackage.packageId}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
            >
              <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2">{unmatchedPackage.packageName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Salesforce room: {unmatchedPackage.roomAssetName}
                </Typography>
              </Stack>

              <FormControl size="small" sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
                <InputLabel id={selectId}>Room type</InputLabel>
                <Select
                  label="Room type"
                  labelId={selectId}
                  value={selectedRoomTypeIdByPackageId[unmatchedPackage.packageId] ?? ''}
                  disabled={disabled}
                  onChange={(event) => onSelectRoomType(unmatchedPackage.packageId, event.target.value)}
                >
                  {roomTypeOptions.map((roomType) => (
                    <MenuItem key={roomType.id} value={roomType.id}>
                      {roomType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
